import { useEffect, useMemo, useState } from "react";
import { api } from "@/context/AuthContext";
import { formatRupiah } from "@/lib/currency";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Wallet, ArrowRight } from "lucide-react";
import KontrakHabisBadge from "@/components/keuangan/KontrakHabisBadge";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell,
} from "recharts";

const PIE_COLORS = ["#DC2626", "#2563EB"];

const RealisasiPie = ({ realisasi, anggaran, label, testId }) => {
  const pct = anggaran > 0 ? Math.round((realisasi / anggaran) * 1000) / 10 : 0;
  const over = realisasi > anggaran && anggaran > 0;
  const data = [{ name: "Realisasi", value: Math.min(realisasi, anggaran) }, { name: "Sisa", value: Math.max(anggaran - realisasi, 0) }];
  return (
    <div className="flex items-center gap-3 rounded-lg border border-slate-100 bg-slate-50/60 px-3 py-2" data-testid={testId}>
      <div className="relative h-[88px] w-[88px] shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} dataKey="value" innerRadius={28} outerRadius={42} startAngle={90} endAngle={-270} stroke="none" isAnimationActive={false}>
              <Cell fill={over ? "#7F1D1D" : PIE_COLORS[0]} /><Cell fill={PIE_COLORS[1]} />
            </Pie>
            <Tooltip formatter={(v) => formatRupiah(v)} />
          </PieChart>
        </ResponsiveContainer>
        <span className={`absolute inset-0 flex items-center justify-center text-xs font-black ${over ? "text-rose-600" : "text-slate-800"}`}>{pct}%</span>
      </div>
      <div className="min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</p>
        <p className="text-xs text-slate-700"><span className="font-semibold text-rose-600">{formatRupiah(realisasi)}</span> <span className="text-slate-400">dari</span> <span className="font-semibold text-blue-600">{formatRupiah(anggaran)}</span></p>
        <p className={`text-[11px] ${over ? "text-rose-600 font-semibold" : "text-slate-400"}`}>{over ? `Melebihi anggaran ${formatRupiah(realisasi - anggaran)}` : `Sisa ${formatRupiah(anggaran - realisasi)}`}</p>
      </div>
    </div>
  );
};

const BIAYA_META = {
  all: { badge: "Total Nilai Kontrak", total: (l) => l.reduce((s, k) => s + (k.nilai_kontrak || 0), 0),
         bars: [["Nilai Kontrak", (k) => k.nilai_kontrak, "#2563EB"], ["Total Realisasi", (k) => k.total_realisasi_fix + k.total_realisasi_at, "#DC2626"]] },
  fix: { badge: "Total Anggaran Fix Cost", total: (l) => l.reduce((s, k) => s + (k.anggaran_fix_cost || 0), 0),
         bars: [["Anggaran Fix", (k) => k.anggaran_fix_cost, "#2563EB"], ["Realisasi Fix", (k) => k.total_realisasi_fix, "#DC2626"]] },
  at: { badge: "Total Anggaran At Cost", total: (l) => l.reduce((s, k) => s + (k.anggaran_at_cost || 0), 0),
        bars: [["Anggaran At", (k) => k.anggaran_at_cost, "#2563EB"], ["Realisasi At", (k) => k.total_realisasi_at, "#DC2626"]] },
};

const TABLE_COLS = {
  fix: ["Kontrak", "Anggaran Fix", "Realisasi Fix", "Sisa Fix"],
  at: ["Kontrak", "Anggaran At", "Realisasi At", "Sisa At"],
  all: ["Kontrak", "Nilai Kontrak", "Anggaran Fix", "Realisasi Fix", "Sisa Fix", "Anggaran At", "Realisasi At", "Sisa At"],
};

// Objek props statis di level modul agar tidak dibuat ulang tiap render (hindari re-render Recharts)
const CHART_MARGIN = { left: 10, right: 20 };
const TICK_STYLE = { fontSize: 11, fill: "#64748B" };
const CURSOR_FILL = { fill: "#F8FAFC" };
const LEGEND_STYLE = { fontSize: 11 };
const BAR_RADIUS = [0, 4, 4, 0];

const KontrakTable = ({ rows, fBiaya }) => (
  <div className="overflow-x-auto rounded-lg border border-slate-100">
    <table className="w-full text-sm" data-testid="keuangan-summary-table">
      <thead>
        <tr className="bg-slate-50 border-b border-slate-200 text-left">
          {(TABLE_COLS[fBiaya] || TABLE_COLS.all).map((h) => (
            <th key={h} className="px-3 py-2.5 font-semibold text-slate-600 text-xs uppercase tracking-wide whitespace-nowrap">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((k) => {
          const sisaFix = k.anggaran_fix_cost - k.total_realisasi_fix;
          const sisaAt = k.anggaran_at_cost - k.total_realisasi_at;
          const sisaCell = (v) => (
            <td className={`px-3 py-2.5 whitespace-nowrap font-semibold ${v < 0 ? "text-rose-600" : "text-emerald-600"}`}>
              {formatRupiah(v)}
            </td>
          );
          return (
            <tr key={k.id} className="border-b border-slate-100" data-testid={`keuangan-summary-row-${k.id}`}>
              <td className="px-3 py-2.5 font-medium text-slate-900 whitespace-nowrap"><span className="inline-flex items-center gap-1.5">{k.nama_kontrak}<KontrakHabisBadge kontrak={k} compact /></span></td>
              {fBiaya === "fix" ? (
                <>
                  <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">{formatRupiah(k.anggaran_fix_cost)}</td>
                  <td className="px-3 py-2.5 text-slate-900 font-medium whitespace-nowrap">{formatRupiah(k.total_realisasi_fix)}</td>
                  {sisaCell(sisaFix)}
                </>
              ) : fBiaya === "at" ? (
                <>
                  <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">{formatRupiah(k.anggaran_at_cost)}</td>
                  <td className="px-3 py-2.5 text-slate-900 font-medium whitespace-nowrap">{formatRupiah(k.total_realisasi_at)}</td>
                  {sisaCell(sisaAt)}
                </>
              ) : (
                <>
                  <td className="px-3 py-2.5 text-slate-700 whitespace-nowrap font-semibold">{formatRupiah(k.nilai_kontrak)}</td>
                  <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">{formatRupiah(k.anggaran_fix_cost)}</td>
                  <td className="px-3 py-2.5 text-slate-900 font-medium whitespace-nowrap">{formatRupiah(k.total_realisasi_fix)}</td>
                  {sisaCell(sisaFix)}
                  <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">{formatRupiah(k.anggaran_at_cost)}</td>
                  <td className="px-3 py-2.5 text-slate-900 font-medium whitespace-nowrap">{formatRupiah(k.total_realisasi_at)}</td>
                  {sisaCell(sisaAt)}
                </>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

export default function KeuanganSummaryCard({ onNavigate, kontrakData = null, subtitle = "" }) {
  const [kontrakList, setKontrakList] = useState([]);
  const [fKontrak, setFKontrak] = useState("aktif"); // aktif | all | <id>
  const [fBiaya, setFBiaya] = useState("all");

  useEffect(() => {
    if (kontrakData) { setKontrakList(kontrakData); return; }
    api.get("/kontrak").then((res) => setKontrakList(res.data)).catch(() => {});
  }, [kontrakData]);

  const hariIni = new Date().toISOString().slice(0, 10);
  const isAktif = (k) => !k.arsip && (!k.periode_selesai || k.periode_selesai >= hariIni);
  const aktifList = useMemo(() => kontrakList.filter(isAktif), [kontrakList]); // eslint-disable-line react-hooks/exhaustive-deps
  const kontrakFiltered = useMemo(
    () => (fKontrak === "all" ? kontrakList : fKontrak === "aktif" ? aktifList : kontrakList.filter((k) => k.id === fKontrak)),
    [fKontrak, kontrakList, aktifList]);
  const biaya = BIAYA_META[fBiaya];
  const chartData = useMemo(() => kontrakFiltered.map((k) => ({
    name: k.nama_kontrak,
    [biaya.bars[0][0]]: biaya.bars[0][1](k),
    [biaya.bars[1][0]]: biaya.bars[1][1](k),
  })), [kontrakFiltered, biaya]);
  const sum = (fn) => kontrakFiltered.reduce((s, k) => s + (fn(k) || 0), 0);
  const pies = [
    ["fix", "Realisasi Fix Cost", sum((k) => k.total_realisasi_fix), sum((k) => k.anggaran_fix_cost)],
    ["at", "Realisasi At Cost", sum((k) => k.total_realisasi_at), sum((k) => k.anggaran_at_cost)],
    ["all", "Realisasi Total Kontrak", sum((k) => k.total_realisasi_fix + k.total_realisasi_at), sum((k) => k.nilai_kontrak)],
  ].filter(([key]) => fBiaya === "all" || key === fBiaya);

  return (
    <Card className="p-6 border-slate-200 shadow-none rounded-lg" data-testid="keuangan-summary-card">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button onClick={() => onNavigate?.("keuangan")} data-testid="goto-keuangan"
          className="flex items-center gap-2 group" title="Buka modul Keuangan">
          <Wallet className="h-4 w-4 text-blue-600" />
          <h3 className="text-base font-semibold text-slate-900 group-hover:text-blue-600 transition-colors">Data Kontrak</h3>
          <ArrowRight className="h-3.5 w-3.5 text-slate-300 group-hover:text-blue-600 group-hover:translate-x-0.5 transition-all" />
        </button>
        <span className="text-xs text-slate-400">{kontrakFiltered.length} kontrak{fKontrak === "aktif" && kontrakList.length > aktifList.length ? ` · ${kontrakList.length - aktifList.length} arsip disembunyikan` : ""}</span>
        <div className="ml-auto rounded-xl bg-blue-50 border border-blue-200 px-5 py-3 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4" data-testid="total-nilai-kontrak">
          <span className="text-xs font-bold uppercase tracking-wider text-blue-600">{biaya.badge}</span>
          <span className="text-2xl font-black text-blue-600 tabular-nums">
            {formatRupiah(biaya.total(kontrakFiltered))}
          </span>
        </div>
      </div>
      {kontrakList.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="keuangan-filter-bar">
          <Select value={fKontrak} onValueChange={setFKontrak}>
            <SelectTrigger className="h-9 w-full sm:w-64" data-testid="keuangan-filter-kontrak">
              <SelectValue placeholder="Nama Kontrak" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="aktif">Kontrak Aktif</SelectItem>
              <SelectItem value="all">Semua Kontrak (termasuk arsip/habis)</SelectItem>
              {kontrakList.map((k) => <SelectItem key={k.id} value={k.id}>{k.nama_kontrak}{isAktif(k) ? "" : " · arsip"}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={fBiaya} onValueChange={setFBiaya}>
            <SelectTrigger className="h-9 w-full sm:w-52" data-testid="keuangan-filter-biaya">
              <SelectValue placeholder="Jenis Biaya" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Biaya</SelectItem>
              <SelectItem value="fix">Anggaran Fix Cost</SelectItem>
              <SelectItem value="at">Anggaran At Cost</SelectItem>
            </SelectContent>
          </Select>
          {(fKontrak !== "aktif" || fBiaya !== "all") && (
            <Button variant="ghost" onClick={() => { setFKontrak("aktif"); setFBiaya("all"); }}
              data-testid="keuangan-filter-reset" className="h-9 text-slate-500 hover:text-rose-600">Reset</Button>
          )}
        </div>
      )}
      {kontrakFiltered.length ? (
        <>
          <div className={`grid gap-3 mb-4 ${pies.length === 1 ? "grid-cols-1 sm:max-w-sm" : "grid-cols-1 sm:grid-cols-3"}`} data-testid="keuangan-realisasi-pies">
            {pies.map(([key, label, real, angg]) => <RealisasiPie key={key} label={label} realisasi={real} anggaran={angg} testId={`keuangan-pie-${key}`} />)}
          </div>
          <div className="mb-5" data-testid="keuangan-summary-chart">
            <ResponsiveContainer width="100%" height={Math.max(120, kontrakFiltered.length * 56)}>
              <BarChart data={chartData} layout="vertical" margin={CHART_MARGIN}>
                <CartesianGrid horizontal={false} stroke="#F1F5F9" />
                <XAxis type="number" tick={TICK_STYLE}
                  tickFormatter={(v) => (v >= 1e9 ? `${v / 1e9} M` : v >= 1e6 ? `${v / 1e6} jt` : v)} />
                <YAxis type="category" dataKey="name" width={130} tick={TICK_STYLE} />
                <Tooltip cursor={CURSOR_FILL} formatter={(v) => formatRupiah(v)} />
                <Legend wrapperStyle={LEGEND_STYLE} />
                <Bar dataKey={biaya.bars[0][0]} fill={biaya.bars[0][2]} radius={BAR_RADIUS} barSize={14} />
                <Bar dataKey={biaya.bars[1][0]} fill={biaya.bars[1][2]} radius={BAR_RADIUS} barSize={14} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <KontrakTable rows={kontrakFiltered} fBiaya={fBiaya} />
        </>
      ) : (
        <p className="text-sm text-slate-400" data-testid="keuangan-summary-empty">
          Belum ada kontrak tercatat. Tambahkan di menu Keuangan.
        </p>
      )}
    </Card>
  );
}
